import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { useAppDispatch } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { localization } from "shared/localization/localization";
import { useTranslation } from "shared/localization/localization";
import { Button, Link } from "shared/ui/Button";
import { Portal } from "shared/ui/Portal";
import {
  isAirRatesDomain,
  isLandRatesDomain,
  isQubictronAiDomain,
  isQubictronDomain,
  QUBICTRON_PROD_ORIGIN,
  SIGN_IN_URL,
} from "shared/utils/url.utils";
import styles from "./RequestLimitWebAppModal.module.scss";

import { appStore } from "../../model/app.store";

type RequestLimitWebAppModalProps = {
  className?: string;
  shipment: ValueOf<typeof ShipmentType>;
  platformId: number;
};

export const RequestLimitWebAppModal: RFC<RequestLimitWebAppModalProps> = ({ shipment, platformId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(appStore.actions.setWebAppRequestLimitReached(false));
  };

  const modalTextMap: Record<ValueOf<typeof ShipmentType>, string> = {
    [ShipmentType.AIR]: t("Please register or login to continue using our Air Tracking"),
    [ShipmentType.SEA]: t("Please register or login to continue using our Container Tracking"),
    [ShipmentType.RAIL]: t("Please register or login to continue using our Rail Tracking"),
    [ShipmentType.ROAD]: t("Please register or login to continue using our Road Tracking"),
    [ShipmentType.PARCEL]: t("Please register or login to continue using our Parcel Tracking"),
  };

  const signInButtonClassName: Record<ValueOf<typeof ShipmentType>, string> = {
    [ShipmentType.AIR]: styles.Air,
    [ShipmentType.SEA]: styles.Sea,
    [ShipmentType.RAIL]: styles.Sea,
    [ShipmentType.ROAD]: styles.Sea,
    [ShipmentType.PARCEL]: styles.Sea,
  };

  // Temporary solution to open the sign in popup
  // TODO: Remove this after the SSO is implemented
  const onSignInPopup = () => {
    window.open(`${QUBICTRON_PROD_ORIGIN}/auth?client=${platformId}`, "SSO");
  };

  const signInControl = (() => {
    const { href, origin } = window.location;
    const lang = localization.language;
    const sameOriginSignInUrl = `${origin}/auth/sign-in`;
    if (isQubictronAiDomain(href)) {
      return (
        <Button size="small" className={cn(styles.link, signInButtonClassName[shipment])} onClick={onSignInPopup}>
          {t("Sign in")}
        </Button>
      );
    }
    if (isQubictronDomain(href)) {
      return (
        <Link size="small" className={cn(styles.link, signInButtonClassName[shipment])} href={SIGN_IN_URL(lang)}>
          {t("Sign in")}
        </Link>
      );
    }
    if (isAirRatesDomain(href) || isLandRatesDomain(href)) {
      return (
        <Link size="small" className={cn(styles.link, signInButtonClassName[shipment])} href={sameOriginSignInUrl}>
          {t("Sign in")}
        </Link>
      );
    }

    return (
      <Link size="small" className={cn(styles.link, signInButtonClassName[shipment])} href={sameOriginSignInUrl}>
        {t("Sign in")}
      </Link>
    );
  })();

  return (
    <Portal className={styles.modal} closePopup={handleClose}>
      <div className={styles.content}>
        <p className={styles.title}>{t("Daily request limit reached")}</p>
        <p className={styles.text}>{modalTextMap[shipment]}</p>
        {signInControl}
      </div>
    </Portal>
  );
};
